import { getClientId } from './GAService';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA;
const GA_API_SECRET = process.env.REACT_APP_GA_API_SECRET;

const STORAGE_KEY = 'offline_ga_queue';
const MAX_QUEUE_SIZE = 300;
const MAX_AGE_MICROS = 72 * 60 * 60 * 1000 * 1000;
const BATCH_SIZE = 25;
const BATCH_INTERVAL_MIN_MS = 3000;
const BATCH_INTERVAL_MAX_MS = 5000;

let queue = [];
let isDraining = false;
let isInitialized = false;
let cancelToken = { cancelled: false };

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.error('[OfflineGA] load failed', err);
    return [];
  }
};

const saveToStorage = (q) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(q));
  } catch (err) {
    console.error('[OfflineGA] persist failed', err);
  }
};

const purgeExpired = (q, nowMicros) =>
  q.filter((e) => e.created_at >= nowMicros - MAX_AGE_MICROS);

const trimToMaxSize = (q) =>
  q.length > MAX_QUEUE_SIZE ? q.slice(q.length - MAX_QUEUE_SIZE) : q;

const randomDelay = () =>
  BATCH_INTERVAL_MIN_MS + Math.random() * (BATCH_INTERVAL_MAX_MS - BATCH_INTERVAL_MIN_MS);

const localISOString = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const absMin = Math.abs(offsetMin);
  const offsetH = pad(Math.floor(absMin / 60));
  const offsetM = pad(absMin % 60);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${offsetH}:${offsetM}`;
};

const sendBatch = async (events) => {
  if (!GA_MEASUREMENT_ID || !GA_API_SECRET) {
    throw new Error('[OfflineGA] missing measurement_id or api_secret');
  }
  const payload = {
    client_id: getClientId(),
    events: events.map((e) => ({
      name: e.name,
      params: e.params,
      timestamp_micros: e.created_at,
    })),
  };
  const url = `https://www.google-analytics.com/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`;
  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`[OfflineGA] sendBatch HTTP ${res.status}`);
  }
};

const sendSyncEvent = async ({ syncedEventCount, reconnectedAt, syncedAt }) => {
  if (!GA_MEASUREMENT_ID || !GA_API_SECRET) {
    console.error('[OfflineGA] sync event missing measurement_id or api_secret');
    return;
  }
  const payload = {
    client_id: getClientId(),
    events: [{
      name: 'offline_data_synced',
      params: {
        synced_event_count: syncedEventCount,
        reconnected_at: reconnectedAt,
        synced_at: syncedAt,
      },
    }],
  };
  const url = `https://www.google-analytics.com/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`;
  try {
    await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[OfflineGA] sync event failed', err);
  }
};

export const enqueue = ({ name, params }) => {
  queue.push({
    name,
    params,
    created_at: Date.now() * 1000,
  });
  queue = trimToMaxSize(queue);
  saveToStorage(queue);
};

export const startDrain = async (reconnectedAt) => {
  if (isDraining) return;
  isDraining = true;
  cancelToken = { cancelled: false };
  const myToken = cancelToken;

  queue = purgeExpired(queue, Date.now() * 1000);
  saveToStorage(queue);
  const initialCount = queue.length;

  while (queue.length > 0 && navigator.onLine && !myToken.cancelled) {
    queue = purgeExpired(queue, Date.now() * 1000);
    saveToStorage(queue);
    if (queue.length === 0) break;

    const batch = queue.slice(0, BATCH_SIZE);
    try {
      await sendBatch(batch);
      queue = queue.slice(batch.length);
      saveToStorage(queue);
    } catch (err) {
      console.error('[OfflineGA] batch failed', err);
      if (!navigator.onLine) {
        break;
      }
      queue = queue.slice(batch.length);
      saveToStorage(queue);
    }

    if (queue.length === 0 || myToken.cancelled || !navigator.onLine) break;
    await new Promise((resolve) => setTimeout(resolve, randomDelay()));
  }

  if (myToken.cancelled) {
    return;
  }

  isDraining = false;

  if (navigator.onLine) {
    const syncedAt = localISOString();
    await sendSyncEvent({
      syncedEventCount: initialCount,
      reconnectedAt,
      syncedAt,
    });
  }
};

export const stopDrain = () => {
  cancelToken.cancelled = true;
  isDraining = false;
};

export const init = () => {
  if (isInitialized) return;
  isInitialized = true;

  queue = loadFromStorage();
  queue = purgeExpired(queue, Date.now() * 1000);
  queue = trimToMaxSize(queue);
  saveToStorage(queue);

  window.addEventListener('online', () => {
    startDrain(localISOString());
  });
  window.addEventListener('offline', () => {
    stopDrain();
  });

  if (navigator.onLine && queue.length > 0) {
    startDrain(localISOString());
  }
};
